import * as React from "react";
import { MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";

type SelectOption = {
    val: string;
    option: string;
};

export interface CclInfoTableEditSelectProps {
    value: string;
    onChange: (newSelection: any, required: boolean) => void;
    options: { val: string; option: string }[] | null;
    required: boolean;
    readonly: boolean;
    empty?: boolean;
    placeholder?: string;
}

const CclInfoTableEditSelect: React.FC<CclInfoTableEditSelectProps> = (props) => {
    const resolveOption = React.useCallback(
        (value: string | null | undefined): SelectOption | null => {
            if (!props.options || value == null) {
                return null;
            }

            const normalizedValue = value.toString().trim().toLowerCase();
            if (!normalizedValue) {
                return null;
            }

            return (
                props.options.find((o) => o.val?.toString().trim().toLowerCase() === normalizedValue) ??
                props.options.find((o) => o.option?.toString().trim().toLowerCase() === normalizedValue) ??
                null
            );
        },
        [props.options]
    );

    const [selected, setSelected] = React.useState<SelectOption | null>(
        resolveOption(props.value) ?? ({ val: "", option: " " } as SelectOption)
    );

    React.useEffect(() => {
        const prop = resolveOption(props.value);
        if (prop != null) {
            setSelected(prop);
            return;
        }

        if (!props.value || props.value.toString().trim() === "") {
            setSelected({ val: "", option: " " });
        }
    }, [props.value, resolveOption]);

    const getOptionFromVal = (val: string): string => {
        if (val == null) return " ";

        let opt = props.options?.find((o) => o.val === val);
        if (opt == null) return " ";

        return opt.option;
    };

    const handleChange = (event: SelectChangeEvent) => {
        let newopt: SelectOption = {
            val: event.target.value,
            option: getOptionFromVal(event.target.value),
        };
        setSelected(newopt);
        if (newopt.val !== "") props.onChange(newopt, props.required);
    };

    if(props.empty) {
        return (
            <Typography sx={{ fontSize: 14, w: 1 }}>
                {" "}
            </Typography>
        );
    }

    if (props.readonly) {
        return (
            <Typography sx={{ fontSize: 14, w: 1 }}>
                {selected?.option ?? ""}
            </Typography>
        );
    }

    return (
        <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={selected?.val ?? ""}
            displayEmpty
            renderValue={(value) => {
                if (!value || value === "")
                    return (
                        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
                            {props.placeholder ?? " "}
                        </Typography>
                    );
                return getOptionFromVal(value);
            }}
            fullWidth={true}
            onChange={handleChange}
            variant={"standard"}
            sx={{ fontSize: 14 }}
        >
            {props.options &&
                props.options.length > 0 &&
                props.options.map((opt, i) => {
                    return (
                        <MenuItem
                            key={opt.val}
                            value={opt.val}
                            selected={opt.val === selected?.val}
                        >
                            {opt.option}
                        </MenuItem>
                    );
                })}
        </Select>
    );
};

export default CclInfoTableEditSelect;
